/**
 * Break-Glass Page — emergency local-credential sign-in
 *
 * The way back in when Twitch OAuth cannot be used: a Twitch outage, a revoked
 * app, a super-admin who has lost access to their Twitch account. Served at
 * `/admin/break-glass` as an UNAUTHENTICATED route, a sibling of `/login` and
 * deliberately OUTSIDE the authed `Layout` shell — the shell's `ProtectedRoute`
 * would bounce the very person this page exists to admit.
 *
 * Consumes `pratbot/api/routes/break_glass.py`:
 *   POST /api/break-glass/login           {username, password}
 *                                         -> {token, expires_in_seconds, auth_method}
 *   POST /api/break-glass/reset-password  {username, code, new_password}
 *                                         -> {reset: true}
 *
 * Note the API prefix is `/api/break-glass`, NOT `/api/admin/break-glass`. The
 * routes are mounted outside the admin router on purpose: that router carries
 * a `SuperAdmin` dependency on every route, and a recovery path gated on the
 * privilege it exists to restore is no recovery path at all. `/admin/break-glass`
 * is this PAGE; the two must not be confused.
 *
 * FAILURES ARE DELIBERATELY INDISTINGUISHABLE. The backend answers 404 when no
 * credential is provisioned, 401 for bad credentials and 429 when rate-limited,
 * and this page renders one identical sentence for all three. The 404 exists so
 * an installation does not confirm the mechanism is even present; a page that
 * said "not configured" — or that said "too many attempts", which only a
 * provisioned installation can ever answer — would hand back exactly the fact
 * the status codes were chosen to withhold. Only client-side validation, which
 * never reaches the network, is allowed to be specific.
 *
 * There is intentionally NO LINK to this page from `Login.tsx`. The URL lives in
 * `architecture/multi-user-architecture.md` instead. A visible link points every
 * passing credential-stuffer at the one account that cannot be de-privileged.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'
import api, { setToken } from '@/api/client'
import { useToast } from '@/components/Toast'

/** Response of `POST /api/break-glass/login`. */
interface BreakGlassTokenResponse {
  token: string
  expires_in_seconds: number
  auth_method: string
}

/** Response of `POST /api/break-glass/reset-password`. */
interface RecoveryResetResponse {
  reset: boolean
}

/**
 * One sentence for every server-side failure — 401, 404, 429, 400, 422 and any
 * transport error alike. See the module docstring: the indistinguishability is
 * the feature.
 */
const SIGN_IN_FAILED = 'Sign-in failed. Check your details and try again.'
const RESET_FAILED = 'Reset failed. Check your details and try again.'

/** Mirrors `RecoveryResetRequest.new_password` (`min_length=12`) server-side. */
const MIN_PASSWORD_LENGTH = 12

type Mode = 'login' | 'recover'

export default function BreakGlass() {
  const navigate = useNavigate()
  const { success, error } = useToast()

  const [mode, setMode] = useState<Mode>('login')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [busy, setBusy] = useState(false)

  /**
   * Authenticate and start a super-admin session.
   *
   * The redirect mirrors `AuthCallback.tsx` exactly, and the reload is
   * load-bearing rather than cosmetic: `AuthProvider` reads the stored token in
   * a MOUNT-TIME effect only, so a client-side `navigate('/')` alone would land
   * on a shell whose `user` is still null, and `ProtectedRoute` would send it
   * straight back to `/login`. The login would have genuinely succeeded and
   * would look like a failure.
   */
  const handleLogin = async () => {
    if (busy) return
    setBusy(true)
    try {
      const data = await api.post<BreakGlassTokenResponse>(
        '/api/break-glass/login',
        { username: username.trim(), password },
      )
      setToken(data.token)
      setTimeout(() => {
        navigate('/', { replace: true })
        window.location.reload()
      }, 100)
    } catch {
      // Nothing is read off the error. Branching on status here would rebuild
      // the oracle the backend's uniform responses exist to prevent.
      error(SIGN_IN_FAILED)
      setBusy(false)
    }
  }

  /**
   * Spend a single-use recovery code to set a new password.
   *
   * Deliberately does NOT sign in afterwards: the backend issues no token here,
   * and requiring a fresh login proves the new password actually works before
   * the operator walks away from the terminal.
   */
  const handleReset = async () => {
    if (busy) return

    // Local-only checks, so these may be specific — they never hit the network
    // and therefore reveal nothing about whether an account exists.
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      error(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (newPassword !== confirmPassword) {
      error('The two new passwords do not match.')
      return
    }

    setBusy(true)
    try {
      await api.post<RecoveryResetResponse>('/api/break-glass/reset-password', {
        username: username.trim(),
        code: code.trim(),
        new_password: newPassword,
      })
      success('Password reset. Sign in with your new password.')
      setMode('login')
      setCode('')
      setNewPassword('')
      setConfirmPassword('')
      setPassword('')
    } catch {
      error(RESET_FAILED)
    } finally {
      setBusy(false)
    }
  }

  const switchMode = () => {
    setMode(mode === 'login' ? 'recover' : 'login')
    setPassword('')
    setCode('')
    setNewPassword('')
    setConfirmPassword('')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-app p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gradient mb-2">PratBot</h1>
          <p className="text-text-secondary">Emergency access</p>
        </div>

        <div className="card">
          <div className="flex items-start gap-3 mb-6">
            <ShieldAlert
              size={20}
              className="text-state-warning shrink-0 mt-0.5"
            />
            <p className="text-sm text-text-secondary">
              Local credentials, for when Twitch sign-in is unavailable. Sessions
              granted here last 60 minutes. Use Twitch sign-in wherever possible.
            </p>
          </div>

          {mode === 'login' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void handleLogin()
              }}
              className="space-y-4"
            >
              <div>
                <label className="label" htmlFor="bg-username">
                  Username
                </label>
                <input
                  id="bg-username"
                  className="input"
                  type="text"
                  autoComplete="username"
                  autoFocus
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div>
                <label className="label" htmlFor="bg-password">
                  Password
                </label>
                <input
                  id="bg-password"
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                className="w-full btn-primary py-3 disabled:opacity-60"
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void handleReset()
              }}
              className="space-y-4"
            >
              <div>
                <label className="label" htmlFor="bg-recover-username">
                  Username
                </label>
                <input
                  id="bg-recover-username"
                  className="input"
                  type="text"
                  autoComplete="username"
                  autoFocus
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div>
                <label className="label" htmlFor="bg-code">
                  Recovery code
                </label>
                <input
                  id="bg-code"
                  className="input"
                  type="text"
                  autoComplete="one-time-code"
                  spellCheck={false}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <p className="help-text">
                  Each code works once. Spending your last one leaves no way back
                  in without the CLI.
                </p>
              </div>

              <div>
                <label className="label" htmlFor="bg-new-password">
                  New password
                </label>
                <input
                  id="bg-new-password"
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <p className="help-text">
                  At least {MIN_PASSWORD_LENGTH} characters.
                </p>
              </div>

              <div>
                <label className="label" htmlFor="bg-confirm-password">
                  Confirm new password
                </label>
                <input
                  id="bg-confirm-password"
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                className="w-full btn-primary py-3 disabled:opacity-60"
              >
                {busy ? 'Resetting…' : 'Reset password'}
              </button>
            </form>
          )}

          <button
            type="button"
            onClick={switchMode}
            disabled={busy}
            className="mt-6 w-full btn-ghost text-sm disabled:opacity-60"
          >
            {mode === 'login'
              ? 'Use a recovery code instead'
              : 'Back to sign in'}
          </button>
        </div>

        <p className="mt-8 text-center text-sm text-text-muted">
          <a href="/login" className="hover:text-text-secondary">
            Sign in with Twitch
          </a>
        </p>
      </div>
    </div>
  )
}
